<template>
  <div class="dp2-wrap">
    <div class="dp2-back cursor-pointer no-print" @click="$emit('back')">
      <i class="el-icon-arrow-left"></i> {{ $t('skills.development.back') }}
    </div>

    <div v-if="rosterLoading && !row" class="dp2-loading flex-mid">
      <loading-even></loading-even>
    </div>
    <div v-else-if="personMissing" class="dp2-muted">{{ $t('skills.development.member_not_found') }}</div>
    <template v-else>
    <div class="dp2-page" v-loading="personLoading">

      <!-- ============================================================ header -->
      <div class="dp2-header">
        <img v-if="row && row.profilepic" v-lazy="row.profilepic" :key="row.profilepic"
          class="image-skeleton dp2-avatar-img flex-mid" :class="[ blurMode ? 'blurelement' : '' ]" />
        <div v-else class="dp2-avatar flex-mid" :class="[ blurMode ? 'blurelement' : '' ]">
          {{ initials(displayName) }}
        </div>
        <div class="dp2-header-text">
          <div class="dp2-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ displayName }}</div>
          <div class="dp2-meta" :class="[ blurMode ? 'blurelement' : '' ]">{{ personEmail }} · {{ periodLabel }}</div>
        </div>
        <div class="dp2-actions no-print">
          <button class="dp2-btn" :class="{ primary: personMonths === 6 }" @click="setMonths(6)">6M</button>
          <button class="dp2-btn" :class="{ primary: personMonths === 12 }" @click="setMonths(12)">12M</button>
          <button class="dp2-btn" @click="printPage">{{ $t('skills.development.export') }}</button>
        </div>
      </div>

      <!-- ================================================ AI summary card -->
      <div class="dp2-card dp2-summary-card">
        <div class="dp2-eyebrow">{{ $t('skills.development.development_summary') }}</div>

        <!-- 1. not enough feedback to ground a summary — no Generate button -->
        <div v-if="needsData" class="dp2-summary-empty">{{ $t('skills.development.needs_data_hint') }}</div>

        <!-- 2. generation in flight -->
        <div v-else-if="pictureStatus === 'generating'" class="dp2-summary-generating">
          <i class="el-icon-loading"></i> {{ $t('skills.development.generating') }}
        </div>

        <!-- 3. generated doc present -->
        <template v-else-if="summary">
          <p v-if="summary.story_lead" class="dp2-story-lead">{{ summary.story_lead }}</p>
          <div class="dp2-sum-grid">
            <div>
              <div class="dp2-sum-label str">{{ $t('skills.development.strengths_themes') }}</div>
              <div v-for="(t, i) in strengthThemes" :key="'sth_' + i" class="dp2-theme">
                <div class="dp2-theme-title">{{ t.title }}</div>
                <div class="dp2-theme-body">{{ t.body }}</div>
              </div>
            </div>
            <div>
              <div class="dp2-sum-label dev">{{ $t('skills.development.development_areas') }}</div>
              <div v-for="(t, i) in growthThemes" :key="'gth_' + i" class="dp2-theme">
                <div class="dp2-theme-title">{{ t.title }}</div>
                <div class="dp2-theme-body">{{ t.body }}</div>
              </div>
            </div>
          </div>
          <div v-if="suggestedFocus.length" class="dp2-focus-row">
            <div class="dp2-sum-label focus">{{ $t('skills.development.suggested_focus') }}</div>
            <div v-for="(f, i) in suggestedFocus" :key="'foc_' + i" class="dp2-focus-item">
              <span class="dp2-focus-dot"></span>
              <div><b>{{ f.focus }}</b><span v-if="f.why" class="dp2-focus-why"> — {{ f.why }}</span></div>
            </div>
          </div>
          <div class="dp2-provenance">{{ provenanceLine }}</div>
          <div v-if="isStale" class="dp2-stale-banner no-print">
            <span>{{ $t('skills.development.stale') }}</span>
            <button class="dp2-btn dp2-btn-sm" @click="generate">{{ $t('skills.development.regenerate') }}</button>
          </div>
        </template>

        <!-- 4. no doc yet (not_generated / none / error) — Generate CTA -->
        <div v-else class="dp2-summary-cta">
          <p class="dp2-summary-hint">{{ $t('skills.development.generate_hint') }}</p>
          <p v-if="pictureStatus === 'error'" class="dp2-summary-error">{{ $t('skills.development.summary_unavailable') }}</p>
          <button class="dp2-btn primary no-print" @click="generate">
            {{ pictureStatus === 'error' ? $t('skills.development.retry') : $t('skills.development.generate_report') }}
          </button>
        </div>
      </div>

      <!-- ================================================ what changed -->
      <div v-if="whatChanged.hasPrior" class="dp2-card dp2-compare-card">
        <div class="dp2-compare-head">
          <div class="dp2-eyebrow">{{ $t('skills.development.what_changed') }}</div>
          <div class="dp2-compare-range">{{ tf('vs_previous_months', { n: personMonths }) }}</div>
        </div>

        <div class="dp2-cmp-hero">
          <div v-if="ratingLevelNow !== null" class="dp2-cmp-hero-item">
            <div class="dp2-cmp-label">{{ $t('skills.development.peer_rating') }}</div>
            <div class="dp2-cmp-val">
              <template v-if="ratingLevelPrev !== null && ratingLevelPrev !== ratingLevelNow">
                <span class="prev">{{ levelLabel(ratingLevelPrev) }}</span><span class="arrow">→</span>{{ levelLabel(ratingLevelNow) }}<span class="dir" :class="ratingLevelNow > ratingLevelPrev ? 'up' : 'down'">{{ ratingLevelNow > ratingLevelPrev ? '▲' : '▼' }}</span>
              </template>
              <template v-else>
                {{ levelLabel(ratingLevelNow) }}<span v-if="ratingLevelPrev !== null" class="hold">{{ $t('skills.development.holding') }}</span>
              </template>
            </div>
          </div>
          <div v-if="whatChanged.mgr.goal" class="dp2-cmp-hero-item">
            <div class="dp2-cmp-label">{{ $t('skills.development.manager_goals') }}</div>
            <div class="dp2-cmp-val dp2-stars">
              <template v-if="whatChanged.mgr.goal.prevLevel !== null && whatChanged.mgr.goal.dir !== 'hold'">
                <span class="prev">{{ gemStarsFor(whatChanged.mgr.goal.prevLevel) }}</span><span class="arrow">→</span>{{ gemStarsFor(whatChanged.mgr.goal.nowLevel) }}<span class="dir" :class="whatChanged.mgr.goal.dir">{{ whatChanged.mgr.goal.dir === 'up' ? '▲' : '▼' }}</span>
              </template>
              <template v-else>
                {{ gemStarsFor(whatChanged.mgr.goal.nowLevel) }}<span v-if="whatChanged.mgr.goal.prevLevel !== null" class="hold">{{ $t('skills.development.holding') }}</span>
              </template>
            </div>
          </div>
          <div v-if="whatChanged.mgr.culture" class="dp2-cmp-hero-item">
            <div class="dp2-cmp-label">{{ $t('skills.development.manager_culture') }}</div>
            <div class="dp2-cmp-val dp2-stars">
              <template v-if="whatChanged.mgr.culture.prevLevel !== null && whatChanged.mgr.culture.dir !== 'hold'">
                <span class="prev">{{ gemStarsFor(whatChanged.mgr.culture.prevLevel) }}</span><span class="arrow">→</span>{{ gemStarsFor(whatChanged.mgr.culture.nowLevel) }}<span class="dir" :class="whatChanged.mgr.culture.dir">{{ whatChanged.mgr.culture.dir === 'up' ? '▲' : '▼' }}</span>
              </template>
              <template v-else>
                {{ gemStarsFor(whatChanged.mgr.culture.nowLevel) }}<span v-if="whatChanged.mgr.culture.prevLevel !== null" class="hold">{{ $t('skills.development.holding') }}</span>
              </template>
            </div>
          </div>
        </div>

        <template v-if="whatChanged.anyMovement">
          <div v-for="mv in whatChanged.movements.improved" :key="'imp_' + mv.name" class="dp2-move">
            <div class="dp2-move-tag improved">{{ $t('skills.development.mv_improved') }}</div>
            <div><span class="dp2-move-skill">{{ mv.name }}</span> <span class="dp2-move-detail">— {{ tf('mv_growth_to_strength', { was: mv.prevImproves, now: mv.nowExcels }) }}</span></div>
          </div>
          <div v-for="mv in whatChanged.movements.newStrengths" :key="'new_' + mv.name" class="dp2-move">
            <div class="dp2-move-tag newstr">{{ $t('skills.development.mv_new_strength') }}</div>
            <div><span class="dp2-move-skill">{{ mv.name }}</span> <span class="dp2-move-detail">— {{ tf('mv_new_strength_detail', { now: mv.nowExcels }) }}</span></div>
          </div>
          <div v-for="mv in whatChanged.movements.stillDeveloping" :key="'still_' + mv.name" class="dp2-move">
            <div class="dp2-move-tag stilldev">{{ $t('skills.development.mv_still_developing') }}</div>
            <div><span class="dp2-move-skill">{{ mv.name }}</span> <span class="dp2-move-detail">— {{ tf('mv_still_detail', { was: mv.prevImproves, now: mv.nowImproves }) }}</span></div>
          </div>
          <div v-for="mv in whatChanged.movements.watch" :key="'watch_' + mv.name" class="dp2-move">
            <div class="dp2-move-tag watch">{{ $t('skills.development.mv_watch') }}</div>
            <div><span class="dp2-move-skill">{{ mv.name }}</span> <span class="dp2-move-detail">— {{ tf('mv_watch_detail', { now: mv.nowImproves }) }}</span></div>
          </div>
        </template>
        <div v-else class="dp2-move-empty">{{ $t('skills.development.consistent_picture') }}</div>

        <div v-if="whatChanged.recognition.now.length" class="dp2-cmp-recognition">
          <span class="dp2-cmp-recog-label">{{ $t('skills.development.recognized_for') }}</span>
          <span v-for="(t, i) in whatChanged.recognition.now" :key="'rt_' + t.tag" class="dp2-rtag">{{ i ? ', ' : ' ' }}{{ t.tag }}<span v-if="t.isNew" class="dp2-rtag-new"> ({{ $t('skills.development.new_short') }})</span></span>
        </div>

        <div class="dp2-cmp-volume">{{ tf('volume_caption', { nowFb: whatChanged.volume.nowFb, nowPeers: whatChanged.volume.nowPeers, nowReviews: whatChanged.volume.nowReviews, prevFb: whatChanged.volume.prevFb, prevPeers: whatChanged.volume.prevPeers, prevReviews: whatChanged.volume.prevReviews }) }}</div>
      </div>

      <!-- ============================================== deterministic brief -->
      <div class="dp2-fallback">
        <span class="dp2-tag">{{ $t('skills.development.deterministic_brief') }}</span>
        <p>
          <b>{{ $t('skills.development.strongest_signal') }}:</b> {{ briefStrongest }}.
          <b>{{ $t('skills.development.most_cited_growth') }}:</b> {{ briefGrowth }}.
          <b>{{ $t('skills.development.rating_label') }}:</b> {{ briefRating }}.
        </p>
      </div>

      <!-- ================================================= what peers said -->
      <div class="dp2-card">
        <div class="dp2-eyebrow">{{ $t('skills.development.what_peers_said') }} · {{ monthsSpanLabel }}</div>
        <div class="dp2-peers-grid">
          <div>
            <div class="dp2-sum-label str">{{ $t('skills.development.strengths_themes') }}</div>
            <div v-if="!sides.strengths.length" class="dp2-muted">{{ $t('skills.development.no_feedback') }}</div>
            <div v-for="s in sides.strengths" :key="'str_' + s.name" class="dp2-skill-block">
              <div class="dp2-skill-head">
                <span class="dp2-skill-name">{{ s.name }}</span>
                <span class="dp2-skill-count">{{ mentionsLabel(s.count) }}</span>
                <span v-if="deltaOf(s, 'str') !== null" class="dp2-delta" :class="deltaClass(s, 'str')">{{ deltaText(s, 'str') }}</span>
                <span v-else-if="isNew(s)" class="dp2-delta up">{{ $t('skills.development.new_label') }}</span>
              </div>
              <div class="dp2-chips">
                <span v-for="b in s.behaviors" :key="'strb_' + b[0]" class="dp2-chip str">{{ b[0] }}{{ b[1] > 1 ? ' ×' + b[1] : '' }}</span>
              </div>
              <div v-if="quotesBySkill[s.name]" class="dp2-quote">
                "{{ quotesBySkill[s.name].quote }}"<span class="dp2-src">{{ paraphrasedFrom(quotesBySkill[s.name].n) }}</span>
              </div>
              <div v-if="s.minority" class="dp2-trend-note">{{ minorityNote(s, 'str') }}</div>
            </div>
          </div>
          <div>
            <div class="dp2-sum-label dev">{{ $t('skills.development.development_areas') }}</div>
            <div v-if="!sides.growth.length" class="dp2-muted">{{ $t('skills.development.no_feedback') }}</div>
            <div v-for="s in sides.growth" :key="'dev_' + s.name" class="dp2-skill-block">
              <div class="dp2-skill-head">
                <span class="dp2-skill-name">{{ s.name }}</span>
                <span class="dp2-skill-count">{{ mentionsLabel(s.count) }}</span>
                <span v-if="deltaOf(s, 'dev') !== null" class="dp2-delta" :class="deltaClass(s, 'dev')">{{ deltaText(s, 'dev') }}</span>
                <span v-else-if="isNew(s)" class="dp2-delta down">{{ $t('skills.development.new_label') }}</span>
              </div>
              <div class="dp2-chips">
                <span v-for="b in s.behaviors" :key="'devb_' + b[0]" class="dp2-chip dev">{{ b[0] }}{{ b[1] > 1 ? ' ×' + b[1] : '' }}</span>
              </div>
              <div v-if="quotesBySkill[s.name]" class="dp2-quote">
                "{{ quotesBySkill[s.name].quote }}"<span class="dp2-src">{{ paraphrasedFrom(quotesBySkill[s.name].n) }}</span>
              </div>
              <div v-if="s.minority" class="dp2-trend-note">{{ minorityNote(s, 'dev') }}</div>
            </div>
          </div>
        </div>
        <!-- Once a generated doc exists, the note describes the AI-paraphrased quotes;
             before that it explains only behaviors + counts are shown. -->
        <div class="dp2-privacy-note">{{ summary ? $t('skills.development.privacy_quotes') : $t('skills.development.privacy_pre_ai') }}</div>
      </div>

      <!-- =============================================== self reflections -->
      <div v-if="reflections.length" class="dp2-card">
        <div class="dp2-eyebrow">{{ $t('skills.development.self_reflection') }}</div>
        <p v-if="summary && summary.reflection_synthesis" class="dp2-story-lead dp2-reflection-lead">{{ summary.reflection_synthesis }}</p>
        <div v-for="(r, i) in reflectionsTop" :key="'refl_' + i" class="dp2-skill-block">
          <div class="dp2-skill-head">
            <span class="dp2-skill-name dp2-reflection-q">{{ r.question }}</span>
            <span class="dp2-skill-count">{{ monthShort(r.month) }} {{ r.date.slice(0, 4) }}</span>
          </div>
          <div class="dp2-quote">
            "{{ reflectionText(r) }}"<span class="dp2-src">{{ $t('skills.development.own_answer_verbatim') }}</span>
          </div>
        </div>
        <div class="dp2-privacy-note">{{ reflectionSourceText }}</div>
      </div>

      <!-- ================================================ development story -->
      <div class="dp2-card">
        <div class="dp2-story-head">
          <div class="dp2-eyebrow dp2-eyebrow-inline">{{ $t('skills.development.development_story') }} · {{ monthsSpanLabel }}</div>
          <div v-if="sparkPts" class="dp2-spark">
            <svg width="120" height="28" viewBox="0 0 120 28" aria-hidden="true">
              <polyline :points="sparkPts" fill="none" stroke="#6C30CC" stroke-width="2" stroke-linecap="round" />
              <circle v-if="sparkLast" :cx="sparkLast.x" :cy="sparkLast.y" r="3" fill="#6C30CC" />
            </svg>
            <span v-if="sparkFromTo" class="dp2-from-to">{{ sparkFromTo }}</span>
          </div>
        </div>

        <!-- feedback loops grid -->
        <div class="dp2-loops">
          <div class="dp2-loops-top">
            <span class="dp2-loops-label">{{ $t('skills.development.feedback_loops') }}</span>
            <span class="dp2-loops-legend">
              <span><span class="dp2-loop-dot"></span>{{ $t('skills.development.peer_feedback_received') }}</span>
              <span v-if="hasMgrRow"><span class="dp2-loop-mgr"></span>{{ $t('skills.development.manager_review_boost') }}</span>
            </span>
          </div>
          <div class="dp2-loops-grid" :style="loopsGridStyle">
            <div></div>
            <div v-for="k in monthKeys" :key="'mh_' + k" class="dp2-loops-monthhead">{{ monthShort(k) }}</div>
            <div></div>
            <template v-for="gr in grid">
              <div class="dp2-loops-row" :key="'lr_' + gr.kind + '_' + gr.label">
                <div class="dp2-loops-name">
                  <span class="dp2-mini-av" :class="{ mgr: gr.kind === 'mgr' }"
                    :style="gr.kind === 'others' ? 'background:#eef0f5;color:#8a8a8a;' : ''">{{ rowAvatar(gr) }}</span>
                  {{ rowLabel(gr) }}
                  <span v-if="gr.kind === 'mgr'" class="dp2-who-role">{{ $t('skills.development.manager_role') }}</span>
                </div>
                <div v-for="k in monthKeys" :key="'lc_' + gr.label + '_' + k" class="dp2-loop-cell">
                  <span v-for="d in cellMarks(gr, k)" :key="d" :class="gr.kind === 'mgr' ? 'dp2-loop-mgr' : 'dp2-loop-dot'"></span>
                </div>
                <div class="dp2-loops-total">×{{ gr.total }}</div>
              </div>
            </template>
          </div>
        </div>

        <!-- growth moments timeline -->
        <div class="dp2-loops-top dp2-moments-head"><span class="dp2-loops-label">{{ $t('skills.development.growth_moments') }}</span></div>
        <div class="dp2-timeline">
          <div v-for="(m, i) in momentsDisplay" :key="'tl_' + i" class="dp2-tl-item">
            <span class="dp2-tl-dot" :class="m.dotClass"></span>
            <div class="dp2-tl-month">{{ monthLong(m.month) }}</div>
            <div class="dp2-tl-title">{{ m.title }}</div>
            <div v-if="m.body" class="dp2-tl-body">{{ m.body }}</div>
            <div v-if="m.strengthsLine" class="dp2-tl-body">{{ m.strengthsLine }}</div>
            <div v-if="m.growthLine" class="dp2-tl-body">{{ m.growthLine }}</div>
            <div v-if="m.givers && m.givers.length" class="dp2-tl-people">
              <span v-for="g in m.givers.slice(0, 4)" :key="'gv_' + g" class="dp2-mini-av">{{ initials(g) }}</span>
              {{ tf('feedback_from', { names: m.givers.join(', ') }) }}
            </div>
          </div>
        </div>
        <div v-if="journeysLineText" class="dp2-privacy-note dp2-journeys-line">{{ journeysLineText }}</div>
      </div>

      <!-- ================================================ footer: balance + boosts -->
      <div class="dp2-foot-grid">
        <div class="dp2-card dp2-card-flush">
          <div class="dp2-eyebrow">{{ $t('skills.development.skill_balance') }}</div>
          <div v-if="!balanceRows.length" class="dp2-muted">{{ $t('skills.development.no_feedback') }}</div>
          <div v-for="b in balanceRows" :key="'bal_' + b.name" class="dp2-bal-row">
            <div class="dp2-bal-name">{{ b.name }}</div>
            <div class="dp2-bal-track">
              <div class="dp2-bal-side dev">
                <template v-if="b.improves">
                  <span class="dp2-bal-n">{{ b.improves }}</span>
                  <div class="dp2-bal-bar dev" :style="{ width: balWidth(b.improves) }"></div>
                </template>
              </div>
              <div class="dp2-bal-axis"></div>
              <div class="dp2-bal-side">
                <template v-if="b.excels">
                  <div class="dp2-bal-bar str" :style="{ width: balWidth(b.excels) }"></div>
                  <span class="dp2-bal-n">{{ b.excels }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="dp2-card dp2-card-flush">
          <div class="dp2-eyebrow">{{ $t('skills.development.manager_review_boosts') }}</div>
          <template v-if="boosts.length">
            <div v-for="(b, i) in boosts" :key="'boost_' + i" class="dp2-cycle">
              <b>{{ monthShort(b.month) }} {{ b.date.slice(0, 4) }}</b>
              <span>{{ $t('skills.development.goals_label') }} {{ gemStarsFor(b.goal) }}</span>
              <span>{{ $t('skills.development.culture_label') }} {{ gemStarsFor(b.culture) }}</span>
              <span>{{ tf('from_name', { name: b.senderName }) }}</span>
            </div>
          </template>
          <div v-else class="dp2-cycle"><span>{{ $t('skills.development.no_manager_reviews') }}</span></div>
        </div>
      </div>

      <!-- ================================================ footnote -->
      <div class="dp2-footnote">{{ footnoteText }}</div>

    </div>
    </template>
  </div>
</template>

<script>
import {
  personPeriodRange, monthKeysBetween, tallyPersonSkills, skillSides,
  ratingLevelsOf, ratingRange, RATING_LEVEL_KEYS,
  mapBoosts, gemStars, mapReflections, mapJourneys, giverGrid,
  deterministicMoments, resolveGrowthMoments, collapseJourneys,
  MOMENT_KIND_CLASS, monthlyLevelSeries, sparklinePoints, computeInputHashV2,
  STALE_AFTER_MS, MIN_FEEDBACK_EVENTS, hasEnoughEvidence,
  skillMovements, managerLevelShift, recognitionTagShift, giverName,
} from "@/utils/developmentPicture";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// An empty-evidence shape so the computeds run before getPersonEvidence resolves.
const EMPTY_EVIDENCE = {
  feedbackAll: [], boostDocs: [], journeyDocs: [], recognitionDocs: [], reflectionDocs: [],
};

export default {
  name: "developmentPerson",
  components: {
    loadingEven: require("../even/loading").default,
  },
  props: { emailKey: { type: String, required: true } },
  computed: {
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    // ---- roster row + guards (preserved from v1) ----
    row() {
      return this.allRows.find(r => r.emailKey === this.emailKey) || null;
    },
    rosterLoading() {
      return this.$store.getters["development/loading"];
    },
    allRows() {
      return this.$store.getters["development/rows"];
    },
    // roster loaded but the deep-linked key isn't in it — bogus ?person= link;
    // without this guard the page would spin forever
    personMissing() {
      return !this.rosterLoading && this.allRows.length > 0 && !this.row;
    },
    // ---- person identity ----
    personEmail() {
      return this.row && this.row.email ? this.row.email : this.emailKey.replace(/\|\|/g, ".");
    },
    displayName() {
      if (this.row) return `${this.row.firstname || ""} ${this.row.lastname || ""}`.trim();
      return this.emailKey.split("||")[0];
    },
    // ---- v2 store state (v2 STATE field, NOT the removed v1 getter) ----
    personMonths() {
      return this.$store.state.development.personMonths;
    },
    personLoading() {
      return this.$store.state.development.personLoading;
    },
    evidence() {
      return this.$store.state.development.personEvidence || EMPTY_EVIDENCE;
    },
    // ---- windows (6M default / 12M toggle re-slices, no refetch) ----
    range() {
      return personPeriodRange(this.personMonths);
    },
    monthKeys() {
      return monthKeysBetween(this.range.start, this.range.end);
    },
    periodLabel() {
      // No dedicated i18n key for the full period label — compose it from the
      // proficiencies preset label (reused for 6M/12M) plus the date range,
      // mirroring v1's periodLabelFor. 6 months → "Past 6-months", else "Past year".
      const presetKey = this.personMonths === 6 ? "past_6_months" : "past_year";
      const preset = this.$t("skills.proficiencies." + presetKey);
      return `${preset} (${this.range.start} – ${this.range.end})`;
    },
    monthsSpanLabel() {
      if (!this.monthKeys.length) return "";
      const first = this.monthKeys[0];
      const last = this.monthKeys[this.monthKeys.length - 1];
      return `${this.monthShort(first)} – ${this.monthShort(last)} ${last.slice(0, 4)}`;
    },
    // ---- feedback windows ----
    fbWin() {
      return (this.evidence.feedbackAll || []).filter(r => r && r.date >= this.range.start && r.date <= this.range.end);
    },
    fbPrev() {
      return (this.evidence.feedbackAll || []).filter(r => r && r.date >= this.range.prevStart && r.date <= this.range.prevEnd);
    },
    hasPrev() {
      return this.fbPrev.length > 0;
    },
    boosts() {
      return mapBoosts(this.evidence.boostDocs).filter(b => b.date >= this.range.start && b.date <= this.range.end);
    },
    journeys() {
      return mapJourneys(this.evidence.journeyDocs, this.range);
    },
    reflections() {
      return mapReflections(this.evidence.reflectionDocs, this.range);
    },
    reflectionsTop() {
      return this.reflections.slice(0, 4);
    },
    recognitionWin() {
      return (this.evidence.recognitionDocs || []).filter(r => r && r.date >= this.range.start && r.date <= this.range.end);
    },
    prevBoosts() {
      return mapBoosts(this.evidence.boostDocs).filter(b => b.date >= this.range.prevStart && b.date <= this.range.prevEnd);
    },
    prevRecognition() {
      return (this.evidence.recognitionDocs || []).filter(r => r && r.date >= this.range.prevStart && r.date <= this.range.prevEnd);
    },
    whatChanged() {
      const hasPrior = this.fbPrev.length > 0 || this.prevBoosts.length > 0 || this.prevRecognition.length > 0;
      const movements = skillMovements(this.tallyPrev, this.tallyNow, 3);
      const anyMovement = !!(movements.improved.length || movements.newStrengths.length
        || movements.stillDeveloping.length || movements.watch.length);
      const distinctPeers = rows => new Set((rows || []).map(r => giverName(r))).size;
      return {
        hasPrior,
        movements,
        anyMovement,
        mgr: managerLevelShift(this.prevBoosts, this.boosts),
        recognition: recognitionTagShift(this.prevRecognition, this.recognitionWin, 3),
        volume: {
          nowFb: this.fbWin.length,
          nowPeers: distinctPeers(this.fbWin),
          nowReviews: this.boosts.length,
          prevFb: this.fbPrev.length,
          prevPeers: distinctPeers(this.fbPrev),
          prevReviews: this.prevBoosts.length,
        },
      };
    },
    // ---- skills ----
    tallyNow() {
      return tallyPersonSkills(this.fbWin);
    },
    tallyPrev() {
      return tallyPersonSkills(this.fbPrev);
    },
    sides() {
      return skillSides(this.tallyNow, this.tallyPrev, 3);
    },
    // ---- brief ----
    briefStrongest() {
      const s = this.sides.strengths[0];
      if (!s) return "—";
      return `${s.name} — ${this.mentionsLabel(s.count)}`;
    },
    briefGrowth() {
      const g = this.sides.growth[0];
      if (!g) return "—";
      return `${g.name} — ${this.mentionsLabel(g.count)}`;
    },
    briefRating() {
      const now = this.ratingLevelNow;
      if (now == null) return "—";
      let text = this.tf("typically", { label: this.levelLabel(now) });
      const prev = this.ratingLevelPrev;
      if (prev != null) {
        text += prev === now
          ? ` (${this.$t("skills.development.holding")})`
          : ` (${this.levelLabel(prev)} → ${this.levelLabel(now)})`;
      }
      return text;
    },
    ratingLevelNow() {
      const lv = ratingLevelsOf(this.fbWin);
      if (!lv.length) return null;
      return Math.round(lv.reduce((a, b) => a + b, 0) / lv.length);
    },
    ratingLevelPrev() {
      const lv = ratingLevelsOf(this.fbPrev);
      if (!lv.length) return null;
      return Math.round(lv.reduce((a, b) => a + b, 0) / lv.length);
    },
    // ---- loops grid ----
    grid() {
      return giverGrid(this.fbWin, this.boosts, this.monthKeys, 6);
    },
    hasMgrRow() {
      return this.grid.some(g => g.kind === "mgr");
    },
    loopsGridStyle() {
      return { gridTemplateColumns: `158px repeat(${this.monthKeys.length}, 1fr) 42px` };
    },
    // ---- growth moments ----
    fallbackMoments() {
      return deterministicMoments({
        monthKeys: this.monthKeys, feedback: this.fbWin, boosts: this.boosts, journeys: this.journeys,
      });
    },
    moments() {
      // aiMoments comes from a generated doc's curated growth_moments; when no
      // doc is present it is null and resolveGrowthMoments falls back to the
      // deterministic set.
      return resolveGrowthMoments(this.aiMoments, {
        monthKeys: this.monthKeys, boosts: this.boosts, feedback: this.fbWin, fallback: this.fallbackMoments,
      });
    },
    momentsDisplay() {
      return this.moments.map(m => this.buildMomentDisplay(m));
    },
    journeysLine() {
      return collapseJourneys(this.journeys, this.moments);
    },
    journeysLineText() {
      const j = this.journeysLine;
      if (!j) return "";
      const cats = j.topCategories.map(([c, n]) => (n > 1 ? `${c} ×${n}` : c)).join(" · ");
      const key = j.inProgress > 0 ? "journeys_also" : "journeys_also_none_progress";
      return this.tf(key, { done: j.done, progress: j.inProgress, cats: cats || "—" });
    },
    // ---- sparkline ----
    levelSeries() {
      return monthlyLevelSeries(this.fbWin, this.monthKeys);
    },
    sparkPts() {
      return sparklinePoints(this.levelSeries, 120, 28);
    },
    sparkLast() {
      if (!this.sparkPts) return null;
      const parts = this.sparkPts.split(" ");
      const [x, y] = parts[parts.length - 1].split(",");
      return { x, y };
    },
    sparkFromTo() {
      const now = this.ratingLevelNow;
      if (now == null) return "";
      const prev = this.ratingLevelPrev;
      if (prev != null && prev !== now) return `${this.levelLabel(prev)} → ${this.levelLabel(now)}`;
      const stars = "★".repeat(now) + "☆".repeat(4 - now);
      return `${stars} ${this.levelLabel(now)}${prev != null ? ` (${this.$t("skills.development.holding")})` : ""}`;
    },
    // ---- skill balance ----
    balanceRows() {
      const t = this.tallyNow;
      return Object.keys(t)
        .map(name => ({ name, excels: t[name].excels, improves: t[name].improves }))
        .sort((a, b) => (b.excels + b.improves) - (a.excels + a.improves))
        .slice(0, 6);
    },
    balanceMax() {
      const t = this.tallyNow;
      let max = 0;
      Object.keys(t).forEach(n => { max = Math.max(max, t[n].excels, t[n].improves); });
      return max;
    },
    // ---- footnote ----
    tagCounts() {
      const counts = {};
      this.recognitionWin.forEach(r => (r.hashtag || []).forEach(h => { if (h) counts[h] = (counts[h] || 0) + 1; }));
      return counts;
    },
    topTags() {
      return Object.entries(this.tagCounts).sort((a, b) => b[1] - a[1]).slice(0, 3);
    },
    givenCount() {
      // feedbackAll is scoped to email_to === person, so "given" isn't in this
      // set; fall back to 0 (full activity lives in Proficiencies per the copy).
      return 0;
    },
    giverCount() {
      const seen = {};
      this.fbWin.forEach(r => { seen[(r.sender_name || String(r.email || "").split("@")[0])] = true; });
      return Object.keys(seen).length;
    },
    footnoteText() {
      const tags = this.topTags.map(([t, n]) => `${t} ×${n}`).join(" · ") || "—";
      return this.tf("activity_footnote", {
        received: this.fbWin.length,
        givers: this.giverCount,
        given: this.givenCount,
        tags,
        events: this.recognitionWin.length,
      });
    },
    reflectionSourceText() {
      return this.tf("reflection_source", { name: this.displayName, n: this.reflections.length });
    },
    // ---- input hash (used by Task 6's Generate flow; computed now for the seam) ----
    inputHash() {
      return computeInputHashV2({
        fb: this.fbWin.length,
        jy: this.journeys.length,
        rec: this.recognitionWin.length,
        boosts: this.boosts.length,
        refl: this.reflections.length,
      });
    },
    // ---- AI narrative (Task 6): cached picture doc + derived slices ----
    pictureDoc() {
      return this.$store.state.development.pictureDoc;
    },
    pictureStatus() {
      return this.$store.state.development.pictureStatus;
    },
    summary() {
      return (this.pictureDoc && this.pictureDoc.summary) || null;
    },
    aiMoments() {
      return this.summary ? this.summary.growth_moments : null;
    },
    quotesBySkill() {
      const map = {};
      // First-write-wins dedupe mirrors the backend's "max one quote per skill"
      // contract — do NOT flip this to last-wins.
      ((this.summary && this.summary.quotes) || []).forEach(q => {
        if (q && q.skill && !map[q.skill]) map[q.skill] = q;
      });
      return map;
    },
    strengthThemes() {
      return (this.summary && this.summary.strength_themes) || [];
    },
    growthThemes() {
      return (this.summary && this.summary.growth_themes) || [];
    },
    suggestedFocus() {
      return (this.summary && this.summary.suggested_focus) || [];
    },
    // hash drift OR 30-day age flips a cached doc to stale (offer Regenerate).
    // An unparseable generatedAt (NaN) is treated as stale — fail toward Regenerate.
    isStale() {
      if (!this.pictureDoc) return false;
      const hashChanged = this.pictureDoc.inputHash !== this.inputHash;
      const t = new Date(this.pictureDoc.generatedAt).getTime();
      const old = !Number.isFinite(t) || Date.now() - t > STALE_AFTER_MS;
      return hashChanged || old;
    },
    // too little evidence to ground a summary — CTA is suppressed. Manager
    // reviews satisfy the gate on their own, so a person with no peer feedback
    // but at least one in-window manager review can still generate.
    needsData() {
      return !hasEnoughEvidence(this.fbWin.length, this.boosts.length);
    },
    provenanceLine() {
      // Compose the AI-summary provenance caption from EXISTING keys only (no new
      // locale keys in this task): `generated_on` + date · `provenance` counts ·
      // `privacy_quotes` (the anonymity note). Mirrors the dev-report treatment.
      const p = (this.pictureDoc && this.pictureDoc.provenance) || {};
      const date = this.pictureDoc && this.pictureDoc.generatedAt
        ? String(this.pictureDoc.generatedAt).slice(0, 10)
        : "";
      const counts = this.tf("provenance", {
        feedback: p.feedback_events != null ? p.feedback_events : this.fbWin.length,
        journeys: p.journeys != null ? p.journeys : this.journeys.length,
        reviews: p.reviews != null ? p.reviews : this.boosts.length,
      });
      const stamp = date ? `${this.$t("skills.development.generated_on")} ${date} · ` : "";
      return `${stamp}${counts} · ${this.$t("skills.development.privacy_quotes")}`;
    },
  },
  watch: {
    // Guard for a future in-place mount: the parent (development.vue) currently
    // uses v-if="personKey", so switching people remounts this component and
    // this watcher never fires today. Kept so that if the parent later reuses
    // the instance (swapping :email-key in place), evidence still reloads.
    emailKey() {
      this.loadPerson();
    },
    // once the roster row for a deep-linked person resolves, ensure evidence loads
    row(newVal, oldVal) {
      if (newVal && !oldVal) this.loadPerson();
    },
  },
  mounted() {
    this.loadPerson();
  },
  beforeDestroy() {
    // Safety net for a mid-print teardown: if the dialog was open when the
    // component was destroyed, afterprint never cleaned up. Always clear the
    // global print class and drop any leaked listener.
    document.body.classList.remove("dp-printing");
    if (this._printCleanup) window.removeEventListener("afterprint", this._printCleanup);
  },
  methods: {
    // manual $t interpolation — the app's $t has no interpolation engine
    tf(key, repl) {
      let s = String(this.$t("skills.development." + key));
      if (repl) Object.keys(repl).forEach(k => { s = s.split("{" + k + "}").join(String(repl[k])); });
      return s;
    },
    initials(name) {
      return String(name || "?").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
    },
    monthShort(k) {
      const m = Number(String(k).slice(5, 7));
      return MONTH_NAMES[m - 1] ? MONTH_NAMES[m - 1].slice(0, 3) : k;
    },
    monthLong(k) {
      const m = Number(String(k).slice(5, 7));
      return MONTH_NAMES[m - 1] || k;
    },
    levelLabel(level) {
      const key = RATING_LEVEL_KEYS[level];
      return key ? this.$t(key) : "";
    },
    ratingPhrase(rows) {
      // Intentional divergence: growth-moment titles show the LEVEL SPREAD for
      // that month (ratingRange lo→hi), while the brief and sparkline show the
      // central level (ratingLevelNow, the rounded mean). Different questions —
      // "what range did peers give?" vs "where does this land?" — so a future
      // reader / Task 6 should NOT reconcile these as a bug.
      const r = ratingRange(rows);
      if (!r) return "";
      return r.lo === r.hi ? this.levelLabel(r.lo) : `${this.levelLabel(r.lo)} → ${this.levelLabel(r.hi)}`;
    },
    gemStarsFor(level) {
      return gemStars(level);
    },
    // ---- what-peers-said helpers ----
    mentionsLabel(n) {
      return this.tf(n === 1 ? "mention_count" : "mentions_count", { n });
    },
    deltaOf(s, side) {
      // strengths compare excels; growth compares improves. new (prev 0) is handled separately.
      if (!this.hasPrev || !s.prev) return null;
      return s.count - s.prev;
    },
    deltaText(s, side) {
      const d = this.deltaOf(s, side);
      if (d === null || d === 0) return "";
      return (d > 0 ? "+" : "−") + Math.abs(d);
    },
    deltaClass(s, side) {
      const d = this.deltaOf(s, side);
      if (d === null) return "";
      // strengths: up is good; growth: fewer mentions is good (invert)
      const good = side === "str" ? d > 0 : d < 0;
      return good ? "up" : "down";
    },
    isNew(s) {
      return this.hasPrev && !s.prev;
    },
    minorityNote(s, side) {
      const key = side === "str" ? "also_growth_note" : "also_praised_note";
      let note = this.tf(key, { n: s.minority });
      if (s.minorityBehavior) note += ` — ${String(s.minorityBehavior).toLowerCase()}`;
      return note + ".";
    },
    // ---- quotes (Task 6) ----
    paraphrasedFrom(n) {
      // A malformed/partial doc may carry a quote with no valid .n — the store
      // only validates that summary is an object. Guard the count: no attribution
      // caption ('') beats rendering "Paraphrased from undefined comments".
      const num = Number(n);
      if (!Number.isFinite(num) || num <= 0) return "";
      // singular gets its own key (no {n}); plural interpolates the count.
      return num === 1
        ? this.$t("skills.development.paraphrased_from_one")
        : this.tf("paraphrased_from", { n: num });
    },
    // ---- reflections ----
    reflectionText(r) {
      const src = r.highlight || r.answer;
      return src.length > 260 ? src.slice(0, 260) + "…" : src;
    },
    // ---- loops grid ----
    rowAvatar(gr) {
      if (gr.kind === "others") return "+" + gr.label;
      return this.initials(gr.label);
    },
    rowLabel(gr) {
      if (gr.kind === "others") {
        return this.tf(Number(gr.label) === 1 ? "other_row" : "others_row", { n: gr.label });
      }
      return gr.label;
    },
    cellMarks(gr, k) {
      const n = gr.months[k] || 0;
      return Math.min(n, 2);
    },
    // ---- growth moments display assembly ----
    buildMomentDisplay(m) {
      const out = {
        month: m.month,
        dotClass: MOMENT_KIND_CLASS[m.kind] || "rt",
        givers: m.givers || null,
        title: "",
        body: "",
        strengthsLine: "",
        growthLine: "",
      };
      if (m.kind === "baseline" || m.kind === "now") {
        // helper leaves title empty and packs "excels|improves" in body
        const rows = this.monthRows(m.month);
        const phrase = this.ratingPhrase(rows);
        const label = this.tf(m.kind === "baseline" ? "baseline_title" : "now_title");
        out.title = phrase ? `${label} · ${phrase}` : label;
        const [excels, improves] = String(m.body || "").split("|");
        out.strengthsLine = this.tf("strengths_cited", { names: excels || "—" });
        out.growthLine = this.tf("growth_cited", { names: improves || "—" });
      } else if (m.kind === "review") {
        // helper already appended the grounded stars ("Goals ★ · Culture ★") to title
        const label = this.tf("manager_review_title");
        out.title = m.title ? `${label} · ${m.title}` : label;
      } else {
        // journey / shift carry real title + body already
        out.title = m.title;
        out.body = m.body;
      }
      return out;
    },
    monthRows(month) {
      return this.fbWin.filter(r => r && String(r.date || "").slice(0, 7) === month);
    },
    // ---- skill balance ----
    balWidth(count) {
      return this.balanceMax > 0 ? Math.round((count / this.balanceMax) * 90) + "%" : "0%";
    },
    // ---- AI narrative (Task 6) ----
    // User-action ONLY (spec decision 13): reachable solely from the Generate/
    // Regenerate buttons — never dispatched on page open. Provenance mirrors the
    // five evidence signals feeding inputHash so the cached doc records its basis.
    generate() {
      this.$store.dispatch("development/generateNarrative", {
        email: this.personEmail,
        inputHash: this.inputHash,
        provenance: {
          feedback_events: this.fbWin.length,
          journeys: this.journeys.length,
          reviews: this.boosts.length,
          recognition_events: this.recognitionWin.length,
          reflections: this.reflections.length,
        },
      });
    },
    // ---- lifecycle ----
    loadPerson() {
      if (this.personMissing) return;
      const email = this.personEmail;
      this.$store.dispatch("development/getPersonEvidence", { email });
      this.$store.dispatch("development/loadPicture", { email });
    },
    // 6M/12M toggle: commit months, reload the cached picture for the new period.
    // Evidence is NOT refetched — the computeds re-slice off the loaded all-time set.
    setMonths(m) {
      if (this.personMonths === m) return;
      this.$store.commit("development/setPersonMonths", m);
      this.$store.dispatch("development/loadPicture", { email: this.personEmail });
    },
    printPage() {
      document.body.classList.add("dp-printing");
      // Keep the cleanup ref on the instance so beforeDestroy can run it if the
      // component is torn down mid-print (parent v-if, Back, route change) —
      // afterprint would never fire then, leaving dp-printing stuck globally.
      this._printCleanup = () => {
        document.body.classList.remove("dp-printing");
        window.removeEventListener("afterprint", this._printCleanup);
        this._printCleanup = null;
      };
      window.addEventListener("afterprint", this._printCleanup);
      window.print();
    },
  },
};
</script>

<style scoped>
.dp2-wrap { margin-top: 24px; font-size: 14px; line-height: 1.6; color: #262626; }
.dp2-back { font-size: 13px; font-weight: 600; color: #6C30CC; margin-bottom: 16px; }
.dp2-loading { padding: 40px 0; }
.dp2-page { max-width: 940px; margin: 0 auto; }
.dp2-muted { font-size: 13px; color: #8a8a8a; padding: 8px 0; }

/* ---- header ---- */
.dp2-header { display: flex; align-items: center; gap: 14px; margin-bottom: 20px; }
.dp2-avatar { width: 48px; height: 48px; border-radius: 50%; background: linear-gradient(135deg, #6C30CC, #8858d5); color: #fff; font-weight: 700; font-size: 16px; flex: none; }
.dp2-avatar-img { width: 48px; height: 48px; border-radius: 50%; object-fit: cover; flex: none; }
.dp2-header-text { display: flex; flex-direction: column; }
.dp2-name { font-size: 20px; font-weight: 700; color: #262626; }
.dp2-meta { font-size: 14px; color: #8a8a8a; }
.dp2-actions { margin-left: auto; display: flex; gap: 8px; }
.dp2-btn { border: 1px solid #d9d9e3; background: #fff; border-radius: 8px; padding: 7px 14px; font-size: 13px; font-family: inherit; color: #404040; cursor: pointer; }
.dp2-btn.primary { background: #6C30CC; border-color: #6C30CC; color: #fff; }

/* ---- cards ---- */
.dp2-card { background: #fff; border: 1px solid #e2e4ea; border-radius: 12px; padding: 24px 28px; margin-bottom: 18px; }
.dp2-card-flush { margin-bottom: 0; }
.dp2-summary-card { background: #faf8fe; border-color: #e6dcf6; }
.dp2-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a8a8a; margin-bottom: 12px; }
.dp2-eyebrow-inline { margin-bottom: 0; }

/* ---- AI summary card (Task 6) ---- */
.dp2-story-lead { font-size: 16px; line-height: 1.7; color: #33254d; margin: 2px 0 20px; }
.dp2-reflection-lead { font-size: 15px; margin-bottom: 16px; }
.dp2-sum-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.dp2-theme { margin-bottom: 14px; }
.dp2-theme-title { font-size: 14px; font-weight: 700; color: #262626; }
.dp2-theme-body { font-size: 14px; color: #4a4a58; line-height: 1.65; margin-top: 3px; }
.dp2-sum-label.focus { color: #6C30CC; }
.dp2-focus-row { margin-top: 18px; padding-top: 16px; border-top: 1px solid #eee6fa; }
.dp2-focus-item { display: flex; align-items: flex-start; gap: 9px; font-size: 14px; line-height: 1.55; padding: 5px 0; color: #33254d; }
.dp2-focus-dot { width: 6px; height: 6px; border-radius: 50%; background: #6C30CC; flex: none; margin-top: 8px; }
.dp2-focus-why { color: #6b6480; font-weight: 400; }
.dp2-provenance { font-size: 12px; color: #8a8a8a; margin-top: 16px; padding-top: 12px; border-top: 1px solid #eee6fa; }
.dp2-summary-empty { font-size: 13px; color: #8a8a8a; }
.dp2-summary-generating { font-size: 14px; color: #6C30CC; display: flex; align-items: center; gap: 8px; }
.dp2-summary-cta { display: flex; flex-direction: column; align-items: flex-start; gap: 12px; }
.dp2-summary-hint { margin: 0; font-size: 14px; color: #595959; line-height: 1.6; }
.dp2-summary-error { margin: 0; font-size: 13px; color: #CC3030; }
.dp2-stale-banner { display: flex; align-items: center; gap: 12px; margin-top: 14px; font-size: 12px; color: #a4761b; }
.dp2-btn-sm { padding: 4px 10px; font-size: 12px; }

/* ---- deterministic brief ---- */
.dp2-fallback { background: #fafbfd; border: 1px dashed #d9d9e3; border-radius: 10px; padding: 14px 18px; margin-bottom: 18px; }
.dp2-tag { display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8a8a8a; background: #eef0f5; border-radius: 6px; padding: 2px 8px; margin-bottom: 8px; }
.dp2-fallback p { margin: 0; font-size: 14px; line-height: 1.65; color: #404040; }

/* ---- what changed (period comparison) ---- */
.dp2-compare-card { padding: 16px 24px 14px; }
.dp2-compare-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; }
.dp2-compare-head .dp2-eyebrow { margin-bottom: 0; }
.dp2-compare-range { font-size: 11px; color: #b8b8c2; }
.dp2-cmp-hero { display: flex; gap: 28px; flex-wrap: wrap; padding-bottom: 12px; border-bottom: 1px solid #eef0f5; margin-bottom: 12px; }
.dp2-cmp-label { font-size: 11px; color: #8a8a8a; margin-bottom: 3px; }
.dp2-cmp-val { font-size: 15px; font-weight: 700; color: #262626; white-space: nowrap; }
.dp2-cmp-val .arrow { color: #b8b8c2; font-weight: 400; padding: 0 3px; }
.dp2-cmp-val .prev { color: #b8b8c2; font-weight: 600; }
.dp2-cmp-val .dir { font-size: 12px; margin-left: 5px; }
.dp2-cmp-val .dir.up { color: #1AC362; }
.dp2-cmp-val .dir.down { color: #F44336; }
.dp2-cmp-val .hold { color: #b8b8c2; font-size: 11px; font-weight: 400; margin-left: 5px; }
.dp2-stars { letter-spacing: 1px; }
.dp2-move { display: flex; align-items: baseline; gap: 10px; padding: 5px 0; font-size: 13.5px; }
.dp2-move-tag { flex: none; width: 122px; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.dp2-move-tag.improved { color: #1AC362; }
.dp2-move-tag.newstr { color: #2f9e63; }
.dp2-move-tag.stilldev { color: #b7791f; }
.dp2-move-tag.watch { color: #CC3030; }
.dp2-move-skill { font-weight: 700; }
.dp2-move-detail { color: #8a8a8a; font-size: 12.5px; }
.dp2-move-empty { font-size: 13px; color: #8a8a8a; padding: 4px 0; }
.dp2-cmp-recognition { font-size: 12.5px; color: #6b6b6b; margin-top: 10px; padding-top: 10px; border-top: 1px solid #eef0f5; }
.dp2-cmp-recog-label { color: #8a8a8a; }
.dp2-rtag-new { color: #6C30CC; font-weight: 600; }
.dp2-cmp-volume { font-size: 11px; color: #b8b8c2; margin-top: 8px; }

/* ---- what peers said ---- */
.dp2-peers-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
.dp2-sum-label { font-size: 13px; font-weight: 700; margin-bottom: 10px; }
.dp2-sum-label.str { color: #1d8a55; }
.dp2-sum-label.dev { color: #a4761b; }
.dp2-skill-block { padding: 14px 0; border-bottom: 1px solid #eef0f5; }
.dp2-skill-block:last-child { border-bottom: none; }
.dp2-skill-head { display: flex; align-items: baseline; gap: 8px; }
.dp2-skill-name { font-size: 15px; font-weight: 700; }
.dp2-reflection-q { font-size: 14px; }
.dp2-skill-count { font-size: 13px; color: #8a8a8a; }
.dp2-delta { font-size: 13px; font-weight: 700; }
.dp2-delta.up { color: #1AC362; }
.dp2-delta.down { color: #F44336; }
.dp2-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 9px 0; }
.dp2-chip { font-size: 12px; padding: 4px 10px; border-radius: 10px; }
.dp2-chip.str { background: #e5f7ee; color: #1d8a55; }
.dp2-chip.dev { background: #fdf3dc; color: #a4761b; }
.dp2-quote { font-size: 14px; color: #4a4a58; font-style: italic; line-height: 1.65; border-left: 3px solid #e6dcf6; padding: 2px 0 2px 13px; margin: 9px 0 2px; }
.dp2-quote .dp2-src { display: block; font-style: normal; font-size: 12px; color: #a0a0ac; margin-top: 4px; }
.dp2-trend-note { font-size: 13px; color: #8a8a8a; margin-top: 7px; }
.dp2-privacy-note { font-size: 12px; color: #8a8a8a; margin-top: 16px; padding-top: 12px; border-top: 1px solid #eef0f5; }
.dp2-journeys-line { margin-top: 16px; }

/* ---- development story ---- */
.dp2-story-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.dp2-spark { display: flex; align-items: center; gap: 10px; font-size: 14px; }
.dp2-spark .dp2-from-to { font-weight: 700; }

.dp2-loops { margin: 2px 0 24px; }
.dp2-loops-top { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px; }
.dp2-moments-head { margin-bottom: 10px; }
.dp2-loops-label { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8a8a8a; }
.dp2-loops-legend { font-size: 12px; color: #8a8a8a; display: flex; gap: 16px; align-items: center; }
.dp2-loops-legend .dp2-loop-dot, .dp2-loops-legend .dp2-loop-mgr { margin-right: 5px; }
.dp2-loops-grid { display: grid; align-items: center; }
.dp2-loops-monthhead { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #a0a0ac; text-align: center; padding-bottom: 6px; }
.dp2-loops-name { font-size: 13px; color: #404040; display: flex; align-items: center; gap: 8px; height: 30px; }
.dp2-loops-name .dp2-who-role { font-size: 11px; color: #a0a0ac; }
.dp2-loops-row { display: contents; }
.dp2-loops-row > * { border-top: 1px solid #f3f4f8; }
.dp2-loop-cell { display: flex; justify-content: center; align-items: center; height: 30px; gap: 3px; }
.dp2-loop-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: #6C30CC; opacity: 0.85; flex: none; }
.dp2-loop-mgr { display: inline-block; width: 9px; height: 9px; background: #F7B924; transform: rotate(45deg); border-radius: 2px; flex: none; }
.dp2-loops-total { font-size: 12px; color: #8a8a8a; text-align: right; height: 30px; display: flex; align-items: center; justify-content: flex-end; }
.dp2-mini-av { width: 22px; height: 22px; border-radius: 50%; background: #ece5f8; color: #6C30CC; font-size: 10px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex: none; }
.dp2-mini-av.mgr { background: #fdf3dc; color: #a4761b; }

.dp2-tl-people { display: flex; align-items: center; gap: 6px; margin-top: 8px; font-size: 12px; color: #8a8a8a; }
.dp2-timeline { position: relative; margin-left: 8px; padding-left: 28px; border-left: 2px solid #eef0f5; }
.dp2-tl-item { position: relative; padding: 0 0 20px; }
.dp2-tl-item:last-child { padding-bottom: 2px; }
.dp2-tl-dot { position: absolute; left: -37px; top: 3px; width: 15px; height: 15px; border-radius: 50%; border: 3px solid #fff; box-shadow: 0 0 0 1px #e2e4ea; }
.dp2-tl-dot.fb { background: #6C30CC; }
.dp2-tl-dot.jy { background: #3AC47D; }
.dp2-tl-dot.rt { background: #c8c9d4; }
.dp2-tl-dot.pf { background: #F7B924; }
.dp2-tl-month { font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8a8a8a; }
.dp2-tl-title { font-size: 15px; font-weight: 700; margin-top: 3px; }
.dp2-tl-body { font-size: 14px; color: #4a4a58; line-height: 1.6; margin-top: 3px; }

/* ---- footer ---- */
.dp2-foot-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 18px; }
.dp2-bal-row { display: flex; align-items: center; padding: 5px 0; }
.dp2-bal-name { width: 140px; font-size: 13px; }
.dp2-bal-track { flex: 1; display: flex; align-items: center; }
.dp2-bal-side { flex: 1; display: flex; align-items: center; gap: 5px; }
.dp2-bal-side.dev { justify-content: flex-end; }
.dp2-bal-axis { width: 1px; height: 14px; background: #e2e4ea; }
.dp2-bal-bar { height: 10px; border-radius: 2px; }
.dp2-bal-bar.str { background: #3AC47D; }
.dp2-bal-bar.dev { background: #F7B924; }
.dp2-bal-n { font-size: 12px; color: #595959; }
.dp2-cycle { display: flex; gap: 10px; font-size: 14px; padding: 5px 0; }
.dp2-cycle b { font-weight: 700; }
.dp2-cycle span { color: #595959; }
.dp2-footnote { font-size: 12px; color: #8a8a8a; margin-top: 16px; line-height: 1.6; }

@media print {
  .no-print { display: none !important; }
  .dp2-card { border: none; padding: 12px 0; page-break-inside: avoid; }
  .dp2-foot-grid { page-break-inside: avoid; }
}
</style>

<!-- Non-scoped print block (carried from v1). Gates on body.dp-printing so it
     only affects the print run. #header is NOT hidden — it holds the router-view. -->
<style>
@media print {
  body.dp-printing .navbar,
  body.dp-printing .mock-navbar,
  body.dp-printing .menu-container,
  body.dp-printing .account-rail {
    display: none !important;
  }
  body.dp-printing .components-container {
    width: 100% !important;
  }
  body.dp-printing .web-page-container.with-account-rail {
    margin-left: 0 !important;
    width: 100% !important;
  }
  body.dp-printing #skills .el-tabs__header,
  body.dp-printing #skills .flex-row-center {
    display: none !important;
  }
  body.dp-printing .dp2-chip,
  body.dp-printing .dp2-bal-bar,
  body.dp-printing .dp2-loop-dot,
  body.dp-printing .dp2-loop-mgr,
  body.dp-printing .dp2-tl-dot,
  body.dp-printing .dp2-mini-av {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
